import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem } from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  ActionKitIntegration,
  ImportCampaignContactsFromActionKitMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import {
  RhfSelect,
  RhfTextField,
  RhfSwitchField,
} from "~frontend/components/forms/react-hook-form-components";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";

import { RhfContactImportFormSubmitButtons } from "../ContactImportFormSubmitButtons";

export const ActionKitFormSchema = z
  .object({
    campaignId: z.string(),
    integrationId: z.string(),
    reportShortName: z.string().min(1, "Required"),
    phoneNumberColumns: z.string().min(1, "Required"),
    vanIdColumn: z.string(),
    enableMultiPhoneNumberInput: z.boolean(),
    vanIntegrationEnabled: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.vanIntegrationEnabled && !data.vanIdColumn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["vanIdColumn"],
      });
    }
  });

export type ActionKitFormInput = z.input<typeof ActionKitFormSchema>;
export type ActionKitFormOutput = z.output<typeof ActionKitFormSchema>;

export const ActionKitFormToApiTransformer = ActionKitFormSchema.transform(
  (data): ImportCampaignContactsFromActionKitMutationVariables["input"] => {
    const { phoneNumberColumns: csvPhoneNumberColumns } = data;
    const phoneNumberColumns = csvPhoneNumberColumns
      .split(",")
      .map(v => v.trim());
    return {
      campaignId: data.campaignId,
      integrationId: data.integrationId,
      reportShortName: data.reportShortName,
      vanIdColumn: data.vanIdColumn,
      phoneNumberColumns,
    };
  }
);

export default function ImportForm({
  onSubmit,
  availableActionKitIntegrations,
  initialValues,
  readOnly,
  resetContactSourceSettings,
}: {
  onSubmit: (values: ActionKitFormOutput) => void;
  availableActionKitIntegrations: ActionKitIntegration[];
  initialValues: ActionKitFormInput;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const isFlagForMultiPhoneNumberEnabled = useFeatureFlag(
    "actionKitMultiPhoneNumberImport"
  );
  const showIntegrationPicker = availableActionKitIntegrations.length != 1;
  const { vanIntegrationEnabled } = initialValues;

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(ActionKitFormSchema),
    defaultValues: initialValues,
  });

  if (!availableActionKitIntegrations.length) {
    return <div />;
  }

  const enableMultiPhoneNumberImport = watch("enableMultiPhoneNumberInput");
  const props = { fullWidth: true, disabled: readOnly, control };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RouteLeavingGuard when={isDirty} />
      {showIntegrationPicker && (
        <RhfSelect label="Integration" name="integrationId" {...props}>
          {availableActionKitIntegrations.map(integration => (
            <MenuItem value={integration.id} key={integration.id}>
              {integration.name}
            </MenuItem>
          ))}
        </RhfSelect>
      )}
      <RhfTextField
        label="ActionKit Report ID"
        name="reportShortName"
        {...props}
      />
      <RhfTextField
        label={`Phone Number ${enableMultiPhoneNumberImport ? "Columns" : "Column"}`}
        name="phoneNumberColumns"
        placeholder={
          enableMultiPhoneNumberImport ? "cell, phone_number" : "cell"
        }
        {...props}
      />
      {vanIntegrationEnabled && (
        <RhfTextField
          label="VAN ID Column"
          name="vanIdColumn"
          placeholder="VAN_ID"
          bottomLabel="Enter the name of the column in your ActionKit Report that contains VAN IDs"
          {...props}
        />
      )}
      {isFlagForMultiPhoneNumberEnabled && (
        <RhfSwitchField
          label={`${enableMultiPhoneNumberImport ? "Disable" : "Enable"} importing multiple phone number columns`}
          name="enableMultiPhoneNumberInput"
          onChange={() => setValue("phoneNumberColumns", "")}
          {...props}
        />
      )}
      <FlexBox justifyContent="flex-end" sx={{ paddingTop: 1 }}>
        <RhfContactImportFormSubmitButtons
          resetContactSourceSettings={resetContactSourceSettings}
          readOnly={readOnly}
          isDirty={isDirty}
          reset={reset}
        />
      </FlexBox>
    </form>
  );
}
